import { DataLayerCallbackProps, DataLayerCallback, DataLayerHistoryItem, DataLayerEvent, DataLayerMeta } from './types'

function executeCallback<Data, Meta>(data: DataLayerCallbackProps<Data, Meta>, fn: DataLayerCallback<Data, Meta>) {
  try {
    fn(data)
  } catch {
    //
  }
}

/**
 * Datalayer, without tight coupling with any analytics provider
 *
 * We will store some base context with agreed data:
 * - page (main title, page url etc)
 *
 * @example:
 * window.dhlDataLayer.subscribe((e) => console.log(e));
 *
 * returns an event with a key, say 'PAGE_LOAD'
 * with this event it returns the metadata agreed to be given for the event key
 * along with our data which is the stored in which
 * you should be able to enrich the data.
 *
 *
 * Exported for typing usage, please use our `initDataLayer` to bootstrap
 */
export class DataLayer<Data, Meta> {
  private history: DataLayerHistoryItem<Meta>[] = []

  private callbacks: DataLayerCallback<Data, Meta>[] = []

  public data: Data | Record<string, never> = {} as Data

  constructor(data: Data | Record<string, never> = {}) {
    this.data = data
  }

  /**
   * Update our stored data
   */
  reset(): void {
    this.data = {}
    this.history = []
  }

  /**
   * Update our stored data
   */
  // eslint-disable-next-line
  update(data: any): void {
    this.data = { ...this.data, ...data }
  }

  /**
   * Get history data
   */
  getHistory(): DataLayerHistoryItem<Meta>[] {
    return [...this.history]
  }

  /**
   * Publish any event
   */
  publish(event: DataLayerEvent, meta: DataLayerMeta<Meta>): void {
    this.history.push({ event, meta })
    this.callbacks.forEach((fn) => executeCallback({ event, meta, data: this.data }, fn))
  }

  /**
   * Subscribe to any tracking event being sent by our application
   * with an option to unsubscibe
   */
  subscribe(fn: DataLayerCallback<Data, Meta>): () => void {
    if (typeof fn === 'function') {
      this.callbacks.push(fn)
      this.history.forEach(({ event, meta }) => executeCallback({ event, meta, data: this.data }, fn))

      return () => {
        // remove the function from the stack
        this.callbacks = this.callbacks.filter((f) => f !== fn)
      }
    }

    return () => undefined
  }
}
